import { computed, onScopeDispose, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { useUi } from '@/composition/use-ui';
import { buildReservationsPublicUrl } from '@/domain/reservations/reservations-public-url';
import { AlertType } from '@/interfaces/IAlert';
import { isShareCancelledError } from '@/lib/invoicing/deliver-pdf';
import { Globals } from '@/modules/Globals';
import { i18n } from '@/plugins/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { createQrBase64 } from '@/utils';
import { copyTextToClipboard } from '@/utils/clipboard.utils';

export interface IReservationsPublicLink {
  url: ComputedRef<string>;
  canShare: ComputedRef<boolean>;
  downloading: Ref<boolean>;
  copy(): Promise<void>;
  share(): Promise<void>;
  downloadQr(): Promise<void>;
  open(): Promise<void>;
}

function nativeShareAvailable(): boolean {
  return Globals.isCapacitor && Capacitor.isPluginAvailable('Share');
}

function webShareAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  );
}

/** The branch's public booking link and the ways the owner hands it out. */
export function useReservationsPublicLink(
  branchId: () => number | null
): IReservationsPublicLink {
  const restaurant = useRestaurantStore();
  const ui = useUi();
  const downloading = ref(false);
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });
  const t = (key: string) => String(i18n.t(key));
  const url = computed(() => {
    const stringId = restaurant.restaurant?.stringId;
    const id = branchId();
    return stringId && id !== null
      ? buildReservationsPublicUrl({
          baseUrl: Globals.shortWebsite,
          stringId,
          branchId: id,
          branchCount: restaurant.branches?.length ?? 1,
        })
      : '';
  });
  const canShare = computed(
    () => nativeShareAvailable() || webShareAvailable()
  );

  async function copy(): Promise<void> {
    if (!url.value) return;
    try {
      await copyTextToClipboard(url.value);
      if (!disposed)
        ui.showToast(AlertType.Success, t('reservations.link.copied'));
    } catch (error) {
      if (!disposed) ui.serverError(error, t('dashboard.urlCopyError'));
    }
  }

  async function share(): Promise<void> {
    if (!url.value) return;
    const title = restaurant.restaurant?.name ?? '';
    try {
      if (nativeShareAvailable()) await Share.share({ title, url: url.value });
      else if (webShareAvailable())
        await navigator.share({ title, url: url.value });
      else await copy();
    } catch (error) {
      if (isShareCancelledError(error) || disposed) return;
      // The share sheet failed for another reason: the link still reaches the owner.
      await copy();
    }
  }

  async function downloadQr(): Promise<void> {
    if (!url.value || downloading.value) return;
    const source = url.value;
    const fileName = `reservas-${restaurant.restaurant?.stringId}-${branchId()}.png`;
    downloading.value = true;
    try {
      const dataUrl = await createQrBase64(source, 600);
      if (disposed || source !== url.value) return;
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = fileName;
      link.click();
    } catch (error) {
      if (!disposed) ui.serverError(error, t('reservations.link.qrError'));
    } finally {
      if (!disposed) downloading.value = false;
    }
  }

  async function open(): Promise<void> {
    if (!url.value) return;
    if (Globals.isCapacitor) await Browser.open({ url: url.value });
    else window.open(url.value, '_blank', 'noopener');
  }

  return { url, canShare, downloading, copy, share, downloadQr, open };
}
